import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Save, FileText, Search, User, Download, Send, Link as LinkIcon, Ban, Sparkles, Languages, Maximize2, Minimize2, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { getAssetUrl } from '../../utils/assetUrl';
import AdminContratoLinksPanel from './AdminContratoLinksPanel';
import { ActionButton, Button, IconButton, Tabs } from '../../components/UI';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { formatMoeda, Moeda } from '../../utils/orcamentoPagamento';

interface ContratoData {
  _id?: string;
  nome: string;
  sobrenome: string;
  dataNascimento: string;
  nacionalidade: string;
  documentoTipo?: 'passaporte' | 'cpf' | 'nif';
  passaporteNumero: string;
  passaporteValidade: string;
  email?: string;
  telefone?: string;
  endereco: {
    logradouro: string;
    numero: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    cep: string;
    pais: string;
  };
  dataContrato: string;
  localAssinatura: string;
  idioma?: 'pt' | 'en' | 'es';
  modelo?: ContratoModelo;
  valorHonorarios?: number;
  formaPagamento?: FormaPagamentoContrato;
  numeroParcelas?: number;
  intervaloDias?: number;
  parcelasPersonalizadas?: ParcelaContrato[];
  tipoPagamento?: string;
  orcamentoId?: string;
  clienteId?: string | null;
  // Moeda e condição (desconto/juros) do orçamento aceito; contratos manuais ficam em €.
  moeda?: Moeda;
  condicaoPagamento?: CondicaoPagamentoContrato | null;
  createdAt?: string;
  status?: 'draft' | 'sent_for_signature' | 'signed' | 'expired' | 'revoked';
  signature?: {
    token?: string;
    expiresAt?: string;
    signedAt?: string;
  };
  signUrl?: string;
}

type FormaPagamentoContrato = 'avista' | 'parcelado' | 'personalizado' | 'parcelado_2' | 'parcelado_3';

interface CondicaoPagamentoContrato {
  tipo: 'avista_desconto' | 'parcelado';
  percentual: number;
  valorBase: number;
}

interface ParcelaContrato {
  valor: number;
  vencimento: string;
}

// parcelado_2/parcelado_3 são o formato antigo (continuam valendo nos contratos já salvos);
// no formulário eles aparecem como "Parcelado" com 2 ou 3 parcelas a cada 30 dias.
const FORMAS_PAGAMENTO: Array<{ value: FormaPagamentoContrato; label: string }> = [
  { value: 'avista', label: 'À vista (integral na assinatura)' },
  { value: 'parcelado', label: 'Parcelado (parcelas iguais, 1ª na assinatura)' },
  { value: 'personalizado', label: 'Parcelas personalizadas (valores e datas)' }
];

const FORMAS_PAGAMENTO_VALIDAS: FormaPagamentoContrato[] = ['avista', 'parcelado', 'personalizado', 'parcelado_2', 'parcelado_3'];

const MIN_PARCELAS_CONTRATO = 2;
const MAX_PARCELAS_CONTRATO = 24;

// Converte o formato antigo para o novo ao abrir no formulário.
const normalizarPlanoContrato = (c: Pick<ContratoData, 'formaPagamento' | 'numeroParcelas' | 'intervaloDias' | 'parcelasPersonalizadas'>) => {
  if (c.formaPagamento === 'parcelado_2' || c.formaPagamento === 'parcelado_3') {
    return { formaPagamento: 'parcelado' as const, numeroParcelas: c.formaPagamento === 'parcelado_2' ? 2 : 3, intervaloDias: 30, parcelasPersonalizadas: [] };
  }
  return {
    formaPagamento: (c.formaPagamento || 'parcelado') as FormaPagamentoContrato,
    numeroParcelas: c.numeroParcelas || 3,
    intervaloDias: c.intervaloDias || 30,
    parcelasPersonalizadas: (c.parcelasPersonalizadas || []).map((p) => ({ valor: Number(p.valor) || 0, vencimento: (p.vencimento || '').slice(0, 10) }))
  };
};

const labelFormaPagamento = (c: Pick<ContratoData, 'formaPagamento' | 'numeroParcelas' | 'intervaloDias' | 'parcelasPersonalizadas'>) => {
  const plano = normalizarPlanoContrato(c);
  if (plano.formaPagamento === 'avista') return 'À vista';
  if (plano.formaPagamento === 'personalizado') return `${plano.parcelasPersonalizadas.length} parcelas personalizadas`;
  return `${plano.numeroParcelas}x a cada ${plano.intervaloDias} dias`;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

// Mesma regra do orçamento, do CRM e da cláusula 3.1: parcelas truncadas no cêntimo, a última absorve a diferença.
const dividirEmParcelas = (total: number, n: number) => {
  const valorParcela = Math.floor((total / n) * 100) / 100;
  return { valorParcela, valorUltima: round2(total - valorParcela * (n - 1)) };
};

const textoParcelasIguais = (total: number, n: number, moeda: Moeda = 'EUR') => {
  const { valorParcela, valorUltima } = dividirEmParcelas(total, n);
  const ultima = Math.abs(valorUltima - valorParcela) >= 0.005 ? ` (a última de ${formatMoeda(valorUltima, moeda)})` : '';
  return `${n} parcelas de ${formatMoeda(valorParcela, moeda)}${ultima}`;
};

const textoCondicaoPagamento = (c: Pick<ContratoData, 'condicaoPagamento' | 'moeda'>) => {
  const cond = c.condicaoPagamento;
  if (!cond || !(cond.percentual > 0)) return '';
  const base = formatMoeda(cond.valorBase, c.moeda || 'EUR');
  return cond.tipo === 'avista_desconto'
    ? `${cond.percentual}% de desconto sobre ${base} para pagamento à vista`
    : `${base} acrescido de ${cond.percentual}% de juros pelo parcelamento`;
};

// A API devolve "Dados inválidos" + a lista do express-validator; mostra o primeiro motivo real.
const mensagemErroSalvar = (error: unknown) => {
  const err = error as { response?: { data?: { message?: string; errors?: Array<{ msg?: string }> } } };
  return err.response?.data?.errors?.[0]?.msg || err.response?.data?.message || 'Erro ao salvar';
};

// Divide o total em n parcelas iguais (a última absorve o arredondamento), a cada `dias` a partir de `inicio`.
const gerarParcelasIguais = (total: number, n: number, inicio: string, dias = 30): ParcelaContrato[] => {
  const valorParcela = Math.floor((total / n) * 100) / 100;
  const base = inicio ? new Date(`${inicio}T12:00:00`) : new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(base);
    d.setDate(d.getDate() + i * dias);
    return {
      valor: i === n - 1 ? round2(total - valorParcela * (n - 1)) : valorParcela,
      vencimento: d.toISOString().slice(0, 10)
    };
  });
};

type TemplateLang = 'pt' | 'en' | 'es';
type ContratoModelo = 'judicial' | 'vistos';

const CONTRATO_MODELOS: ContratoModelo[] = ['judicial', 'vistos'];
const MODELO_LABEL: Record<ContratoModelo, string> = { judicial: 'Judicial', vistos: 'Assessoria de Vistos' };
const MODELO_BADGE_CLASS: Record<ContratoModelo, string> = {
  judicial: 'bg-indigo-100 text-indigo-700',
  vistos: 'bg-sky-100 text-sky-700'
};

interface ContratoTemplateInfo {
  modelo: ContratoModelo;
  lang: TemplateLang;
  status: 'not_generated' | 'default' | 'draft' | 'active';
  generatedAt: string | null;
  activatedAt: string | null;
}

const TEMPLATE_LANG_LABEL: Record<TemplateLang, string> = { pt: 'Português', en: 'Inglês', es: 'Espanhol' };
const TEMPLATE_STATUS_LABEL: Record<ContratoTemplateInfo['status'], string> = {
  not_generated: 'Não gerado',
  default: 'Texto padrão do sistema',
  draft: 'Rascunho',
  active: 'Ativo'
};

interface TemplateSegment {
  kind: 'title' | 'clause' | 'item' | 'rich';
  label: string;
  text: string;
  // Segmentos que aceitam incluir/remover (seção, cláusula numerada, item de lista).
  structural?: 'section' | 'clause' | 'item';
}

interface TemplateEditState {
  doc: Document;
  setters: Array<(text: string) => void>;
  elements: Element[];
  originalPlaceholderCount: number;
}

function countPlaceholders(html: string): number {
  return (html.match(/\{\{[^}]*\}\}/g) || []).length;
}

const PLACEHOLDER_SPLIT_RE = /(\{\{[^}]*\}\})/;
const BOLD_SPLIT_RE = /(\*\*[^*]+?\*\*)/;

// Rótulos dos parágrafos do bloco de partes, pela posição (a estrutura é a mesma em pt/en/es).
const PARTIES_LABELS = [
  'Partes: abertura',
  'Qualificação do CONTRATADO',
  'Partes: ligação',
  'Qualificação do CONTRATANTE',
  'Partes: texto que introduz as cláusulas'
];

// Parágrafo "rico" ↔ texto editável: <strong> vira **texto**, <span class="highlight">
// vira o próprio marcador {{...}}. Espaços e quebras são colapsados.
function serializeRich(el: Element): string {
  return Array.from(el.childNodes)
    .map((node) => (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'STRONG'
      ? `**${node.textContent || ''}**`
      : node.textContent || ''))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

// Só vale a pena editar o que tem prosa; linhas feitas só de marcadores e pontuação
// (endereço do cabeçalho, local/data, nome sob a assinatura) ficam de fora.
function hasProse(text: string): boolean {
  return /[A-Za-zÀ-ÿ]/.test(text.replace(/\{\{[^}]*\}\}/g, '').replace(/\*\*/g, ''));
}

function rebuildRich(doc: Document, el: Element, text: string, highlighted: Set<string>) {
  while (el.firstChild) el.removeChild(el.firstChild);
  text.split(BOLD_SPLIT_RE).forEach((chunk) => {
    if (!chunk) return;
    const bold = chunk.match(/^\*\*([^*]+?)\*\*$/);
    if (bold) {
      const strong = doc.createElement('strong');
      strong.textContent = bold[1];
      el.appendChild(strong);
      return;
    }
    chunk.split(PLACEHOLDER_SPLIT_RE).forEach((part) => {
      if (!part) return;
      if (highlighted.has(part)) {
        const span = doc.createElement('span');
        span.className = 'highlight';
        span.textContent = part;
        el.appendChild(span);
      } else {
        el.appendChild(doc.createTextNode(part));
      }
    });
  });
}

// Extrai da doc parseada (em ordem) todo o texto revisável: título do documento,
// cabeçalho (editado uma vez e aplicado a todas as páginas), bloco das partes, títulos
// de seção, cláusulas numeradas (pulando as 100% dinâmicas, ex.: {{honorariosClausula31}}),
// itens de lista, texto final e rótulos das assinaturas. A estrutura (cláusulas, numeração,
// condicionais) nunca é tocada: só o texto dentro de cada elemento.
function extractTemplateSegments(doc: Document): { segments: TemplateSegment[]; setters: Array<(text: string) => void>; elements: Element[] } {
  const segments: TemplateSegment[] = [];
  const setters: Array<(text: string) => void> = [];
  const elements: Element[] = [];

  const pushRich = (label: string, els: Element[]) => {
    const text = serializeRich(els[0]);
    if (!hasProse(text)) return;
    const highlighted = new Set(
      Array.from(els[0].querySelectorAll('span.highlight')).map((span) => span.textContent?.trim() || '')
    );
    segments.push({ kind: 'rich', label, text });
    setters.push((next) => els.forEach((el) => rebuildRich(doc, el, next, highlighted)));
    elements.push(els[0]);
  };

  const headers = Array.from(doc.body.querySelectorAll('header.page-header'));
  const headerLines = (h: Element) => Array.from(h.querySelectorAll(':scope > p'));
  const firstHeaderLines = headers[0] ? headerLines(headers[0]) : [];
  const otherHeaderLines = new Set(headers.slice(1).reduce<Element[]>((acc, h) => acc.concat(headerLines(h)), []));
  const partiesLines = Array.from(doc.body.querySelectorAll('.parties-block > p'));

  doc.body.querySelectorAll('h1.document-title, p, li').forEach((el) => {
    if (otherHeaderLines.has(el)) return;

    if (el.tagName === 'H1') {
      const text = el.textContent?.trim() || '';
      segments.push({ kind: 'title', label: 'Título do documento', text });
      setters.push((next) => { el.textContent = next; });
      elements.push(el);
      return;
    }

    const headerIdx = firstHeaderLines.indexOf(el);
    if (headerIdx >= 0) {
      const copies = headers.slice(1).map((h) => headerLines(h)[headerIdx]).filter(Boolean);
      pushRich(`Cabeçalho, linha ${headerIdx + 1} (repete em todas as páginas)`, [el, ...copies]);
      return;
    }

    const partiesIdx = partiesLines.indexOf(el);
    if (partiesIdx >= 0) {
      pushRich(PARTIES_LABELS[partiesIdx] || 'Bloco das partes', [el]);
      return;
    }

    if (el.tagName === 'LI') {
      const text = el.textContent?.trim() || '';
      if (!text) return;
      segments.push({ kind: 'item', label: 'Item da lista', text, structural: 'item' });
      setters.push((next) => { el.textContent = next; });
      elements.push(el);
      return;
    }

    if (el.classList.contains('clause-title')) {
      const text = el.textContent?.trim() || '';
      segments.push({ kind: 'title', label: 'Título da seção', text, structural: 'section' });
      setters.push((next) => { el.textContent = next; });
      elements.push(el);
      return;
    }

    const numSpan = el.querySelector('span.clause-num');
    if (numSpan) {
      const clauseLabel = numSpan.textContent?.trim() || '';
      const fullText = el.textContent?.trim() || '';
      const bodyText = fullText.startsWith(clauseLabel) ? fullText.slice(clauseLabel.length).trim() : fullText;
      if (/^\{\{[^}]*\}\}$/.test(bodyText)) return;
      segments.push({ kind: 'clause', label: `Cláusula ${clauseLabel}`, text: bodyText, structural: 'clause' });
      elements.push(el);
      setters.push((next) => {
        Array.from(el.childNodes).forEach((child) => {
          if (child !== numSpan) el.removeChild(child);
        });
        el.appendChild(doc.createTextNode(` ${next}`));
      });
      return;
    }

    pushRich(el.closest('.sign-block') ? 'Assinatura: rótulo' : 'Texto final', [el]);
  });

  return { segments, setters, elements };
}

// ——— Estrutura do modelo: incluir/remover seções, cláusulas e itens, e renumerar ———

// Blocos das cláusulas em ordem de documento: títulos de seção, cláusulas numeradas e listas.
// Uma seção pode continuar depois de uma quebra de página (outro <section>), por isso a
// ordem é a do documento, não a do pai.
function templateBlocks(doc: Document): Element[] {
  return Array.from(doc.body.querySelectorAll('p, ol')).filter((el) =>
    el.classList.contains('clause-title') || el.tagName === 'OL' || !!el.querySelector(':scope > span.clause-num')
  );
}

const isSectionTitle = (el: Element) => el.classList.contains('clause-title');

// Títulos "N. Texto" e cláusulas "N.M" voltam a ficar em sequência.
function renumberTemplate(doc: Document) {
  let section = 0;
  let clause = 0;
  templateBlocks(doc).forEach((el) => {
    if (isSectionTitle(el)) {
      section += 1;
      clause = 0;
      el.textContent = (el.textContent || '').replace(/^\s*\d+\s*\.\s*/, `${section}. `);
      return;
    }
    const num = el.querySelector(':scope > span.clause-num');
    if (num) {
      clause += 1;
      num.textContent = `${section}.${clause}`;
    }
  });
}

function newClause(doc: Document, text: string) {
  const p = doc.createElement('p');
  const num = doc.createElement('span');
  num.className = 'clause-num';
  p.appendChild(num);
  p.appendChild(doc.createTextNode(` ${text}`));
  return p;
}

const NOVA_CLAUSULA = 'Nova cláusula: escreva o texto aqui.';

// Inclui uma cláusula depois de `el` (cláusula: depois dela e da lista que a acompanha;
// título: logo abaixo do título, como primeira cláusula da seção).
function addClauseAfter(doc: Document, el: Element) {
  const anchor = !isSectionTitle(el) && el.nextElementSibling?.tagName === 'OL' ? el.nextElementSibling : el;
  anchor.after(newClause(doc, NOVA_CLAUSULA));
}

function addSectionAfter(doc: Document, titleEl: Element) {
  const blocks = templateBlocks(doc);
  const start = blocks.indexOf(titleEl);
  const nextTitle = blocks.findIndex((b, i) => i > start && isSectionTitle(b));
  const last = nextTitle >= 0 ? blocks[nextTitle - 1] : blocks[blocks.length - 1];
  const title = doc.createElement('p');
  title.className = 'clause-title no-indent';
  title.textContent = '0. Nova seção';
  const clause = newClause(doc, NOVA_CLAUSULA);
  last.after(title, clause);
}

// Remover nunca pode levar junto um marcador {{...}} (ex.: a cláusula dos honorários).
function removalError(els: Element[]): string | null {
  const html = els.map((el) => el.outerHTML).join('');
  return countPlaceholders(html) > 0
    ? 'Este trecho tem marcadores {{...}} preenchidos automaticamente (ex.: honorários) e não pode ser removido. Edite o texto em vez de remover.'
    : null;
}

function removeClause(el: Element): string | null {
  const els = el.nextElementSibling?.tagName === 'OL' ? [el, el.nextElementSibling] : [el];
  const err = removalError(els);
  if (err) return err;
  els.forEach((e) => e.remove());
  return null;
}

function removeSection(doc: Document, titleEl: Element): string | null {
  const blocks = templateBlocks(doc);
  const start = blocks.indexOf(titleEl);
  const nextTitle = blocks.findIndex((b, i) => i > start && isSectionTitle(b));
  const els = blocks.slice(start, nextTitle >= 0 ? nextTitle : blocks.length);
  if (blocks.filter(isSectionTitle).length <= 1) return 'O contrato precisa ter pelo menos uma seção.';
  const err = removalError(els);
  if (err) return err;
  els.forEach((e) => e.remove());
  return null;
}

function addItemAfter(doc: Document, li: Element) {
  const item = doc.createElement('li');
  item.textContent = 'Novo item';
  li.after(item);
}

function removeItem(li: Element): string | null {
  const list = li.parentElement;
  const err = removalError([li]);
  if (err) return err;
  li.remove();
  if (list && list.tagName === 'OL' && !list.querySelector('li')) list.remove();
  return null;
}

interface AdvogadoOption {
  _id: string;
  nome: string;
  oabNumero: string;
  oabEstado: string;
  cedulaProfissional: string;
  logo?: string;
}

const emptyForm = (): ContratoData => ({
  nome: '',
  sobrenome: '',
  dataNascimento: '',
  nacionalidade: 'brasileira',
  documentoTipo: 'passaporte',
  passaporteNumero: '',
  passaporteValidade: '',
  email: '',
  telefone: '',
  endereco: {
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    cep: '',
    pais: 'Portugal'
  },
  dataContrato: new Date().toISOString().split('T')[0],
  localAssinatura: 'Vagos',
  idioma: 'pt',
  modelo: 'judicial',
  valorHonorarios: 600,
  formaPagamento: 'parcelado',
  numeroParcelas: 3,
  intervaloDias: 30,
  parcelasPersonalizadas: [],
  tipoPagamento: ''
});

interface PrefillContrato {
  // Na moeda do orçamento, exatamente como o cliente aceitou.
  valorHonorarios?: number;
  moeda?: Moeda;
  condicaoPagamento?: CondicaoPagamentoContrato | null;
  formaPagamento?: string;
  numeroParcelas?: number;
  intervaloDias?: number;
  parcelasPersonalizadas?: ParcelaContrato[];
  orcamentoId?: string;
  clienteId?: string;
  nomeCliente?: string;
  modelo?: string;
}

interface ClienteCrm {
  _id: string;
  nome: string;
  email?: string;
  telefone?: string;
  dataNascimento?: string;
  nacionalidade?: string;
  passaporteNumero?: string;
  passaporteValidade?: string;
  nif?: string;
  cpf?: string;
  endereco?: Partial<ContratoData['endereco']>;
}

// Copia para o contrato os dados do cliente do CRM. Campos vazios no CRM não apagam o que já foi digitado.
function clienteParaContrato(cliente: ClienteCrm, prev: ContratoData): ContratoData {
  const v = (valor: string | undefined, atual: string) => (valor && valor.trim() ? valor.trim() : atual);
  const data = (valor: string | undefined, atual: string) => (valor ? valor.slice(0, 10) : atual);
  const [nome, ...resto] = (cliente.nome || '').trim().split(/\s+/);

  let documento: Pick<ContratoData, 'documentoTipo' | 'passaporteNumero' | 'passaporteValidade'> = {
    documentoTipo: prev.documentoTipo,
    passaporteNumero: prev.passaporteNumero,
    passaporteValidade: prev.passaporteValidade
  };
  if (cliente.passaporteNumero?.trim()) {
    documento = { documentoTipo: 'passaporte', passaporteNumero: cliente.passaporteNumero.trim(), passaporteValidade: data(cliente.passaporteValidade, '') };
  } else if (cliente.nif?.trim()) {
    documento = { documentoTipo: 'nif', passaporteNumero: cliente.nif.trim(), passaporteValidade: '' };
  } else if (cliente.cpf?.trim()) {
    documento = { documentoTipo: 'cpf', passaporteNumero: cliente.cpf.trim(), passaporteValidade: '' };
  }

  const e = cliente.endereco || {};
  return {
    ...prev,
    clienteId: cliente._id,
    nome: v(nome, prev.nome),
    sobrenome: resto.length ? resto.join(' ') : prev.sobrenome,
    dataNascimento: data(cliente.dataNascimento, prev.dataNascimento),
    nacionalidade: v(cliente.nacionalidade, prev.nacionalidade),
    email: v(cliente.email, prev.email || ''),
    telefone: v(cliente.telefone, prev.telefone || ''),
    ...documento,
    endereco: {
      logradouro: v(e.logradouro, prev.endereco.logradouro),
      numero: v(e.numero, prev.endereco.numero),
      complemento: v(e.complemento, prev.endereco.complemento || ''),
      bairro: v(e.bairro, prev.endereco.bairro),
      cidade: v(e.cidade, prev.endereco.cidade),
      cep: v(e.cep, prev.endereco.cep),
      pais: v(e.pais, prev.endereco.pais)
    }
  };
}

// Busca um cliente do CRM para preencher o contrato e vinculá-lo.
const ClienteCrmPicker: React.FC<{
  clienteId?: string | null;
  onSelect: (cliente: ClienteCrm) => void;
  onUnlink: () => void;
}> = ({ clienteId, onSelect, onUnlink }) => {
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<ClienteCrm[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [vinculado, setVinculado] = useState<ClienteCrm | null>(null);
  const [trocando, setTrocando] = useState(false);

  useEffect(() => {
    if (!clienteId) { setVinculado(null); return; }
    if (vinculado?._id === clienteId) return;
    axios.get(`/clientes/${clienteId}`)
      .then((res) => setVinculado(res.data.cliente))
      .catch(() => setVinculado(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 2) { setResultados([]); return; }
    const timer = setTimeout(async () => {
      try {
        setBuscando(true);
        const res = await axios.get('/clientes', { params: { search: termo, limit: 8 } });
        setResultados(res.data.clientes || []);
      } catch {
        setResultados([]);
      } finally {
        setBuscando(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [busca]);

  const escolher = (cliente: ClienteCrm) => {
    setVinculado(cliente);
    setTrocando(false);
    setBusca('');
    setResultados([]);
    onSelect(cliente);
  };

  if (clienteId && !trocando) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 mb-4">
        <div className="flex-1 text-sm text-gray-800">
          Vinculado ao cliente do CRM: <strong>{vinculado?.nome || '…'}</strong>
        </div>
        <div className="flex gap-2">
          {vinculado && (
            <Button size="sm" variant="secondary" type="button" onClick={() => onSelect(vinculado)}>
              Copiar dados de novo
            </Button>
          )}
          <Button size="sm" variant="outline" type="button" onClick={() => setTrocando(true)}>Trocar</Button>
          <Button size="sm" variant="ghost" type="button" onClick={() => { setVinculado(null); onUnlink(); }}>Desvincular</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">Copiar dados de um cliente do CRM</label>
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={busca}
          onChange={(ev) => setBusca(ev.target.value)}
          placeholder="Buscar por nome, e-mail, telefone, passaporte, NIF ou CPF"
          className="field pl-10"
        />
      </div>
      {busca.trim().length >= 2 && (
        <div className="mt-1 rounded-lg border border-gray-200 bg-white shadow-sm max-h-64 overflow-y-auto">
          {buscando && <div className="px-4 py-2 text-sm text-gray-500">Buscando…</div>}
          {!buscando && resultados.length === 0 && <div className="px-4 py-2 text-sm text-gray-500">Nenhum cliente encontrado.</div>}
          {resultados.map((c) => (
            <button
              key={c._id}
              type="button"
              onClick={() => escolher(c)}
              className="block w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
            >
              <div className="text-sm font-medium text-gray-900">{c.nome}</div>
              <div className="text-xs text-gray-500">
                {[c.email, c.telefone, c.passaporteNumero && `Passaporte ${c.passaporteNumero}`, c.nif && `NIF ${c.nif}`, c.cpf && `CPF ${c.cpf}`].filter(Boolean).join(' · ') || 'Sem outros dados'}
              </div>
            </button>
          ))}
        </div>
      )}
      {clienteId && trocando && (
        <button type="button" className="mt-1 text-xs text-gray-500 underline" onClick={() => setTrocando(false)}>Cancelar troca</button>
      )}
    </div>
  );
};

const AdminContrato: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [contratos, setContratos] = useState<ContratoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modeloFilter, setModeloFilter] = useState<ContratoModelo | ''>('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ContratoData>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [showAdvogadoModal, setShowAdvogadoModal] = useState(false);
  const [pendingContratoId, setPendingContratoId] = useState<string | null>(null);
  const [advogadosList, setAdvogadosList] = useState<AdvogadoOption[]>([]);
  const [selectedAdvogadoId, setSelectedAdvogadoId] = useState<string | null>(null);

  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureModalMode, setSignatureModalMode] = useState<'send' | 'link'>('send');
  const [signatureContratoId, setSignatureContratoId] = useState<string | null>(null);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [sendingSignature, setSendingSignature] = useState(false);
  const [signUrlPreview, setSignUrlPreview] = useState('');

  const [templates, setTemplates] = useState<ContratoTemplateInfo[]>([]);
  // Modelo exibido no painel de modelos/idiomas; generating/toggling guardam `${modelo}:${lang}`.
  const [templateModelo, setTemplateModelo] = useState<ContratoModelo>('judicial');
  const [generatingTemplateKey, setGeneratingTemplateKey] = useState<string | null>(null);
  const [togglingTemplateKey, setTogglingTemplateKey] = useState<string | null>(null);
  const [editingTemplateLang, setEditingTemplateLang] = useState<TemplateLang | null>(null);
  const [editingTemplateModelo, setEditingTemplateModelo] = useState<ContratoModelo>('judicial');
  const [templateSegments, setTemplateSegments] = useState<TemplateSegment[]>([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const templateEditRef = useRef<TemplateEditState | null>(null);

  const { isExpanded: isAdvogadoModalExpanded, setIsExpanded: setIsAdvogadoModalExpanded, toggleExpanded: toggleAdvogadoModalExpanded } = useExpandable();
  useEscapeKey(() => setShowAdvogadoModal(false), showAdvogadoModal);

  const { isExpanded: isSignatureModalExpanded, setIsExpanded: setIsSignatureModalExpanded, toggleExpanded: toggleSignatureModalExpanded } = useExpandable();
  useEscapeKey(() => setShowSignatureModal(false), showSignatureModal);

  const { isExpanded: isTemplateModalExpanded, setIsExpanded: setIsTemplateModalExpanded, toggleExpanded: toggleTemplateModalExpanded } = useExpandable();
  useEscapeKey(() => { setEditingTemplateLang(null); templateEditRef.current = null; }, !!editingTemplateLang);

  const notifications = useNotifications();
  const confirm = useConfirm();

  useEffect(() => {
    fetchContratos();
  }, [searchTerm, modeloFilter]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Chegou aqui a partir da conversão de um Orçamento — pré-preenche o formulário
  // com valor/forma de pagamento e mantém o vínculo (orcamentoId) para completar
  // os dados legais (passaporte, endereço) que o orçamento não coleta.
  useEffect(() => {
    const navState = location.state as { prefillContrato?: PrefillContrato; editContratoId?: string } | null;

    // "Refazer contrato" de um orçamento que já tem contrato salvo: abre esse contrato para edição.
    if (navState?.editContratoId) {
      const id = navState.editContratoId;
      navigate(location.pathname, { replace: true, state: null });
      axios.get<ContratoData>(`/contrato/${id}`)
        .then((res) => openEdit(res.data))
        .catch(() => notifications.showError('Contrato não encontrado', 'Não foi possível abrir o contrato deste orçamento.'));
      return;
    }

    const prefill = navState?.prefillContrato;
    if (!prefill) return;

    const formaPagamentoValida = FORMAS_PAGAMENTO_VALIDAS.includes(prefill.formaPagamento as FormaPagamentoContrato)
      ? (prefill.formaPagamento as FormaPagamentoContrato)
      : 'avista';

    const [nome, ...resto] = (prefill.nomeCliente || '').trim().split(/\s+/);

    setEditingId(null);
    setFormData({
      ...emptyForm(),
      nome: nome || '',
      sobrenome: resto.join(' '),
      valorHonorarios: prefill.valorHonorarios ?? 600,
      ...normalizarPlanoContrato({
        formaPagamento: formaPagamentoValida,
        numeroParcelas: prefill.numeroParcelas,
        intervaloDias: prefill.intervaloDias,
        parcelasPersonalizadas: prefill.parcelasPersonalizadas
      }),
      moeda: prefill.moeda || 'EUR',
      condicaoPagamento: prefill.condicaoPagamento || null,
      orcamentoId: prefill.orcamentoId,
      clienteId: prefill.clienteId || null,
      modelo: CONTRATO_MODELOS.includes(prefill.modelo as ContratoModelo) ? (prefill.modelo as ContratoModelo) : 'judicial'
    });
    setShowForm(true);

    // Cliente do CRM conhecido (orçamento ou "Novo contrato" no CRM): copia os dados dele.
    if (prefill.clienteId) {
      axios.get(`/clientes/${prefill.clienteId}`)
        .then((res) => setFormData((prev) => clienteParaContrato(res.data.cliente, prev)))
        .catch(() => { /* segue só com o nome */ });
    }

    // Limpa o state da navegação para não reaplicar o prefill num refresh/voltar.
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get<ContratoTemplateInfo[]>('/contrato-templates');
      setTemplates(res.data);
    } catch (error) {
      console.error('Erro ao carregar modelos de contrato:', error);
    }
  };

  const handleGenerateTemplate = async (modelo: ContratoModelo, lang: TemplateLang) => {
    try {
      setGeneratingTemplateKey(`${modelo}:${lang}`);
      await axios.post(`/contrato-templates/${modelo}/${lang}/generate`);
      notifications.showSuccess('Rascunho gerado!', 'Revise o conteúdo antes de ativar.');
      fetchTemplates();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.showError('Erro ao gerar tradução', err.response?.data?.message || 'Tente novamente.');
    } finally {
      setGeneratingTemplateKey(null);
    }
  };

  const openEditTemplate = async (modelo: ContratoModelo, lang: TemplateLang) => {
    try {
      const res = await axios.get(`/contrato-templates/${modelo}/${lang}`);
      const html: string = res.data.html || '';
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const { segments, setters, elements } = extractTemplateSegments(doc);

      templateEditRef.current = { doc, setters, elements, originalPlaceholderCount: countPlaceholders(html) };
      setTemplateSegments(segments);
      setEditingTemplateModelo(modelo);
      setEditingTemplateLang(lang);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.showError('Erro ao carregar modelo', err.response?.data?.message || 'Tente novamente.');
    }
  };

  const updateTemplateSegment = (index: number, text: string) => {
    setTemplateSegments((prev) => prev.map((seg, i) => (i === index ? { ...seg, text } : seg)));
  };

  // Aplica o texto digitado até aqui, muda a estrutura, renumera e reextrai os segmentos.
  const changeTemplateStructure = (index: number, action: (doc: Document, el: Element) => string | null | void) => {
    const state = templateEditRef.current;
    if (!state) return;
    templateSegments.forEach((seg, i) => state.setters[i]?.(seg.text));
    const error = action(state.doc, state.elements[index]);
    if (error) {
      notifications.showError('Não foi possível remover', error);
      return;
    }
    renumberTemplate(state.doc);
    const { segments, setters, elements } = extractTemplateSegments(state.doc);
    templateEditRef.current = { ...state, setters, elements };
    setTemplateSegments(segments);
  };

  const renderStructureActions = (seg: TemplateSegment, index: number) => {
    if (!seg.structural) return null;
    const btn = 'link-action text-xs text-primary-700 hover:underline';
    const danger = 'link-action text-xs text-red-600 hover:underline';
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
        {seg.structural === 'section' && (
          <>
            <button type="button" className={btn} onClick={() => changeTemplateStructure(index, addClauseAfter)}>+ cláusula no início da seção</button>
            <button type="button" className={btn} onClick={() => changeTemplateStructure(index, addSectionAfter)}>+ seção depois desta</button>
            <button type="button" className={danger} onClick={async () => (await confirm({ title: 'Remover esta seção e todas as cláusulas dela?', confirmText: 'Remover', variant: 'danger' })) && changeTemplateStructure(index, removeSection)}>Remover seção</button>
          </>
        )}
        {seg.structural === 'clause' && (
          <>
            <button type="button" className={btn} onClick={() => changeTemplateStructure(index, addClauseAfter)}>+ cláusula abaixo</button>
            <button type="button" className={danger} onClick={async () => (await confirm({ title: 'Remover esta cláusula?', confirmText: 'Remover', variant: 'danger' })) && changeTemplateStructure(index, (_doc, el) => removeClause(el))}>Remover cláusula</button>
          </>
        )}
        {seg.structural === 'item' && (
          <>
            <button type="button" className={btn} onClick={() => changeTemplateStructure(index, addItemAfter)}>+ item abaixo</button>
            <button type="button" className={danger} onClick={() => changeTemplateStructure(index, (_doc, el) => removeItem(el))}>Remover item</button>
          </>
        )}
      </div>
    );
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplateLang || !templateEditRef.current) return;
    const { doc, setters, originalPlaceholderCount } = templateEditRef.current;

    templateSegments.forEach((seg, i) => setters[i]?.(seg.text));
    const html = `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;

    if (countPlaceholders(html) !== originalPlaceholderCount) {
      notifications.showError(
        'Não foi possível salvar',
        'A edição parece ter alterado um marcador {{...}} do modelo. Desfaça a alteração e tente novamente.'
      );
      return;
    }

    try {
      setSavingTemplate(true);
      await axios.put(`/contrato-templates/${editingTemplateModelo}/${editingTemplateLang}`, { html });
      notifications.success.settingsUpdated();
      setEditingTemplateLang(null);
      templateEditRef.current = null;
      fetchTemplates();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.showError('Erro ao salvar modelo', err.response?.data?.message || 'Tente novamente.');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleToggleTemplateActive = async (modelo: ContratoModelo, lang: TemplateLang, activate: boolean) => {
    try {
      setTogglingTemplateKey(`${modelo}:${lang}`);
      await axios.post(`/contrato-templates/${modelo}/${lang}/${activate ? 'activate' : 'deactivate'}`);
      notifications.success.settingsUpdated();
      fetchTemplates();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.showError('Erro', err.response?.data?.message || 'Tente novamente.');
    } finally {
      setTogglingTemplateKey(null);
    }
  };

  const fetchContratos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '50' });
      if (searchTerm) params.set('search', searchTerm);
      if (modeloFilter) params.set('modelo', modeloFilter);
      const res = await axios.get(`/contrato/list?${params}`);
      setContratos(res.data.contratos);
    } catch (error) {
      console.error(error);
      notifications.error.settingsUpdate('Erro ao carregar contratos');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm());
    setShowForm(true);
  };

  const openEdit = (c: ContratoData) => {
    setEditingId(c._id || null);
    setFormData({
      ...c,
      dataNascimento: c.dataNascimento.split('T')[0],
      documentoTipo: c.documentoTipo || 'passaporte',
      passaporteValidade: c.passaporteValidade ? c.passaporteValidade.split('T')[0] : '',
      dataContrato: c.dataContrato ? c.dataContrato.split('T')[0] : new Date().toISOString().split('T')[0],
      endereco: { ...c.endereco },
      valorHonorarios: c.valorHonorarios ?? 600,
      ...normalizarPlanoContrato(c),
      tipoPagamento: c.tipoPagamento || '',
      modelo: c.modelo || 'judicial'
    });
    setShowForm(true);
  };

  const somaParcelas = round2((formData.parcelasPersonalizadas || []).reduce((acc, p) => acc + (Number(p.valor) || 0), 0));
  const somaParcelasOk = Math.abs(somaParcelas - Number(formData.valorHonorarios ?? 0)) <= 0.01;

  const changeFormaPagamento = (forma: FormaPagamentoContrato) => {
    setFormData(prev => {
      const next = { ...prev, formaPagamento: forma };
      // Ao entrar em "personalizado" sem parcelas, parte do parcelamento atual para o admin só ajustar.
      if (forma === 'personalizado' && !(prev.parcelasPersonalizadas || []).length) {
        const n = prev.formaPagamento === 'parcelado' ? Math.max(prev.numeroParcelas || 3, 1) : 1;
        next.parcelasPersonalizadas = gerarParcelasIguais(Number(prev.valorHonorarios ?? 0), n, prev.dataContrato, prev.intervaloDias || 30);
      }
      return next;
    });
  };

  const updateParcela = (index: number, patch: Partial<ParcelaContrato>) => {
    setFormData(prev => ({
      ...prev,
      parcelasPersonalizadas: (prev.parcelasPersonalizadas || []).map((p, i) => (i === index ? { ...p, ...patch } : p))
    }));
  };

  const addParcela = () => {
    setFormData(prev => {
      const lista = prev.parcelasPersonalizadas || [];
      const ultima = lista[lista.length - 1];
      const d = ultima?.vencimento ? new Date(`${ultima.vencimento}T12:00:00`) : new Date();
      if (ultima) d.setDate(d.getDate() + 30);
      const restante = round2(Number(prev.valorHonorarios ?? 0) - lista.reduce((acc, p) => acc + (Number(p.valor) || 0), 0));
      return { ...prev, parcelasPersonalizadas: [...lista, { valor: restante > 0 ? restante : 0, vencimento: d.toISOString().slice(0, 10) }] };
    });
  };

  const removeParcela = (index: number) => {
    setFormData(prev => ({ ...prev, parcelasPersonalizadas: (prev.parcelasPersonalizadas || []).filter((_, i) => i !== index) }));
  };

  const validarPlanoPagamento = () => {
    // Contrato de orçamento: o plano vem travado da escolha do cliente e o servidor recalcula.
    if (formData.orcamentoId) return true;
    if (formData.formaPagamento === 'personalizado' && !somaParcelasOk) {
      notifications.error.settingsUpdate(`A soma das parcelas (€${somaParcelas.toFixed(2)}) precisa ser igual ao valor dos honorários.`);
      return false;
    }
    return true;
  };

  const handleField = (field: string, value: string) => {
    if (field.startsWith('endereco.')) {
      const key = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        endereco: { ...prev.endereco, [key]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validarPlanoPagamento()) return;
    try {
      setSaving(true);
      if (editingId) {
        await axios.put(`/contrato/${editingId}`, formData);
      } else {
        await axios.post('/contrato', formData);
      }
      notifications.success.settingsUpdated();
      setShowForm(false);
      fetchContratos();
    } catch (error: unknown) {
      notifications.error.settingsUpdate(mensagemErroSalvar(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: 'Excluir contrato', message: 'Este registro será excluído permanentemente.', confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/contrato/${id}`);
      notifications.success.settingsUpdated();
      fetchContratos();
    } catch {
      notifications.error.settingsUpdate('Erro ao excluir');
    }
  };

  const openGeneratePdf = async (contratoId: string) => {
    try {
      const res = await axios.get<AdvogadoOption[]>('/advogado/all');
      if (res.data.length === 0) {
        notifications.error.settingsUpdate('Cadastre um advogado em Admin → Advogados');
        return;
      }
      setAdvogadosList(res.data);
      setSelectedAdvogadoId(res.data[0]._id);
      setPendingContratoId(contratoId);
      setShowAdvogadoModal(true);
    } catch {
      notifications.error.settingsUpdate('Erro ao carregar advogados');
    }
  };

  const generatePdf = async () => {
    if (!pendingContratoId || !selectedAdvogadoId) return;
    const contrato = contratos.find(c => c._id === pendingContratoId);
    try {
      setGeneratingId(pendingContratoId);
      setShowAdvogadoModal(false);
      const apiUrl = axios.defaults.baseURL || '/api';
      const query = new URLSearchParams({ advogadoId: selectedAdvogadoId });
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${apiUrl}/contrato/${pendingContratoId}/contrato-pdf?${query}`,
        { headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf' } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrato-${contrato?.nome || 'cliente'}-${contrato?.sobrenome || ''}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      notifications.success.settingsUpdated();
    } catch (error: unknown) {
      const err = error as Error;
      notifications.error.settingsUpdate('Erro ao gerar PDF: ' + err.message);
    } finally {
      setGeneratingId(null);
      setPendingContratoId(null);
    }
  };

  const openSendForSignature = (contratoId: string) => {
    setSignatureContratoId(contratoId);
    setSignatureModalMode('send');
    setExpiresInDays(30);
    setSignUrlPreview('');
    setShowSignatureModal(true);
  };

  const openViewSignatureLink = async (contratoId: string) => {
    try {
      const res = await axios.get(`/contrato/${contratoId}`);
      setSignatureContratoId(contratoId);
      setSignatureModalMode('link');
      setSignUrlPreview(res.data.signUrl || '');
      setShowSignatureModal(true);
    } catch {
      notifications.error.settingsUpdate('Erro ao carregar link de assinatura');
    }
  };

  // enviarEmail=false só gera o link, para o admin mandar por WhatsApp etc. (cliente sem e-mail).
  const confirmSendForSignature = async (enviarEmail: boolean) => {
    if (!signatureContratoId) return;
    try {
      setSendingSignature(true);
      const res = await axios.post(`/contrato/${signatureContratoId}/send-for-signature`, { expiresInDays, enviarEmail });
      setSignUrlPreview(res.data.signUrl);
      setSignatureModalMode('link');
      if (res.data.emailEnviado) {
        notifications.showSuccess('Link enviado por e-mail', 'O cliente recebeu o link para assinar.');
      } else if (enviarEmail) {
        notifications.showWarning('Link gerado, mas o e-mail falhou', 'Copie o link e envie por outro canal.');
      } else {
        notifications.showSuccess('Link gerado', 'Copie o link e envie ao cliente.');
      }
      fetchContratos();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      notifications.error.settingsUpdate(err.response?.data?.message || 'Erro ao enviar contrato para assinatura');
    } finally {
      setSendingSignature(false);
    }
  };

  const { copy, isCopied } = useCopyToClipboard();
  const linkCopied = isCopied();
  const copySignUrl = () => {
    if (signUrlPreview) copy(signUrlPreview);
  };

  // Contrato aberto no modal de assinatura: e-mail habilita o envio, telefone habilita o WhatsApp.
  const signatureContrato = contratos.find(c => c._id === signatureContratoId);
  const signatureTemEmail = !!signatureContrato?.email?.trim();
  const signatureWhatsapp = (signatureContrato?.telefone || '').replace(/\D/g, '');
  const whatsappSignHref = signatureWhatsapp && signUrlPreview
    ? `https://wa.me/${signatureWhatsapp}?text=${encodeURIComponent(
      `Olá${signatureContrato?.nome ? `, ${signatureContrato.nome}` : ''}! Segue o link para revisar e assinar o seu contrato: ${signUrlPreview}`
    )}`
    : '';

  const revokeSignatureLink = async () => {
    if (!signatureContratoId) return;
    if (!(await confirm({ title: 'Revogar link', message: 'O cliente não poderá mais assinar este contrato.', confirmText: 'Revogar', variant: 'danger' }))) return;
    try {
      await axios.post(`/contrato/${signatureContratoId}/revoke-signature-link`);
      notifications.success.settingsUpdated();
      setShowSignatureModal(false);
      fetchContratos();
    } catch {
      notifications.error.settingsUpdate('Erro ao revogar link');
    }
  };

  const downloadSignedPdf = async (contratoId: string) => {
    const contrato = contratos.find(c => c._id === contratoId);
    try {
      setGeneratingId(contratoId);
      const apiUrl = axios.defaults.baseURL || '/api';
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${apiUrl}/contrato/${contratoId}/contrato-pdf`,
        { headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf' } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contrato-assinado-${contrato?.nome || 'cliente'}-${contrato?.sobrenome || ''}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      notifications.success.settingsUpdated();
    } catch (error: unknown) {
      const err = error as Error;
      notifications.error.settingsUpdate('Erro ao baixar contrato assinado: ' + err.message);
    } finally {
      setGeneratingId(null);
    }
  };

  const saveAndGenerate = async () => {
    if (!validarPlanoPagamento()) return;
    try {
      setSaving(true);
      let id = editingId;
      if (editingId) {
        await axios.put(`/contrato/${editingId}`, formData);
      } else {
        const res = await axios.post('/contrato', formData);
        id = res.data.contrato._id;
      }
      setShowForm(false);
      await fetchContratos();
      if (id) await openGeneratePdf(id);
    } catch (error: unknown) {
      notifications.error.settingsUpdate(mensagemErroSalvar(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Breadcrumbs />
      </div>

      <AdminContratoLinksPanel />

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('admin.contract')}</h1>
          <p className="text-gray-600 mt-1 text-sm">
            Formulário administrativo para gerar o contrato de prestação de serviços com advogado selecionado.
          </p>
        </div>
        {!showForm && (
          <Button
            onClick={openCreate}>
            <Plus className="w-4 h-4" />
            Novo Cliente
          </Button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-1">
          <Languages className="w-5 h-5" /> Modelos de contrato / idiomas
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Cada modelo tem o seu próprio texto. Edite as cláusulas em português a qualquer momento. Para inglês/espanhol, gere rascunhos com IA a partir do texto em português do mesmo modelo, revise o conteúdo e só depois ative — só idiomas ativos ficam disponíveis para escolher em um novo contrato.
        </p>
        <Tabs
          variant="segmented"
          className="mb-4"
          ariaLabel="Modelo de contrato"
          value={templateModelo}
          onChange={setTemplateModelo}
          items={CONTRATO_MODELOS.map((modelo) => ({ id: modelo, label: MODELO_LABEL[modelo] }))}
        />
        <div className="space-y-3">
          {(['pt', 'en', 'es'] as TemplateLang[]).map((lang) => {
            const info = templates.find((tpl) => tpl.modelo === templateModelo && tpl.lang === lang);
            const status = info?.status || (lang === 'pt' ? 'default' : 'not_generated');
            const key = `${templateModelo}:${lang}`;
            return (
              <div key={lang} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-900">{TEMPLATE_LANG_LABEL[lang]}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    status === 'active' ? 'bg-green-100 text-green-700'
                      : status === 'draft' ? 'bg-yellow-100 text-yellow-700'
                      : status === 'default' ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {TEMPLATE_STATUS_LABEL[status]}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {lang !== 'pt' && (
                    <button
                      type="button"
                      onClick={() => handleGenerateTemplate(templateModelo, lang)}
                      disabled={generatingTemplateKey === key}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50"
                    >
                      <Sparkles className="w-4 h-4" />
                      {generatingTemplateKey === key ? 'Gerando...' : status === 'not_generated' ? 'Gerar com IA' : 'Regenerar com IA'}
                    </button>
                  )}
                  {(status !== 'not_generated' || lang === 'pt') && (
                    <Button variant="ghost" size="sm"
                      type="button"
                      onClick={() => openEditTemplate(templateModelo, lang)}>
                      Editar
                    </Button>
                  )}
                  {status === 'draft' && (
                    <Button size="sm"
                      type="button"
                      onClick={() => handleToggleTemplateActive(templateModelo, lang, true)}
                      disabled={togglingTemplateKey === key}>
                      Ativar
                    </Button>
                  )}
                  {status === 'active' && (
                    <Button variant="ghost" size="sm" className="text-red-700 hover:bg-red-50 border-red-200"
                      type="button"
                      onClick={() => handleToggleTemplateActive(templateModelo, lang, false)}
                      disabled={togglingTemplateKey === key}>
                      Desativar
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
            <h2 className="text-xl font-semibold">
              {editingId ? 'Editar dados do contratante' : 'Novo contratante'}
            </h2>
            <IconButton action="close" onClick={() => setShowForm(false)} />
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" /> Dados do contratante
              </h3>
              <ClienteCrmPicker
                clienteId={formData.clienteId}
                onSelect={(cliente) => setFormData((prev) => clienteParaContrato(cliente, prev))}
                onUnlink={() => setFormData((prev) => ({ ...prev, clienteId: null }))}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input required value={formData.nome} onChange={e => handleField('nome', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sobrenome *</label>
                  <input required value={formData.sobrenome} onChange={e => handleField('sobrenome', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data de nascimento *</label>
                  <input type="date" required value={formData.dataNascimento}
                    onChange={e => handleField('dataNascimento', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nacionalidade</label>
                  <input value={formData.nacionalidade} onChange={e => handleField('nacionalidade', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de documento *</label>
                  <select
                    required
                    value={formData.documentoTipo || 'passaporte'}
                    onChange={e => handleField('documentoTipo', e.target.value)}
                    className="field field--select"
                  >
                    <option value="passaporte">Passaporte</option>
                    <option value="cpf">CPF</option>
                    <option value="nif">NIF</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nº do documento {(formData.documentoTipo || 'passaporte') === 'passaporte' ? '(passaporte)' : formData.documentoTipo === 'cpf' ? '(CPF)' : '(NIF)'} *
                  </label>
                  <input required value={formData.passaporteNumero}
                    onChange={e => handleField('passaporteNumero', e.target.value)}
                    className="field" />
                </div>
                {(formData.documentoTipo || 'passaporte') === 'passaporte' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Validade passaporte *</label>
                    <input type="date" required value={formData.passaporteValidade}
                      onChange={e => handleField('passaporteValidade', e.target.value)}
                      className="field" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={formData.email || ''} onChange={e => handleField('email', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input value={formData.telefone || ''} onChange={e => handleField('telefone', e.target.value)}
                    className="field" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Domicílio (Portugal)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logradouro *</label>
                  <input required value={formData.endereco.logradouro}
                    onChange={e => handleField('endereco.logradouro', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número *</label>
                  <input required value={formData.endereco.numero}
                    onChange={e => handleField('endereco.numero', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                  <input value={formData.endereco.complemento || ''}
                    onChange={e => handleField('endereco.complemento', e.target.value)}
                    placeholder="Bloco, andar, etc."
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Freguesia *</label>
                  <input required value={formData.endereco.bairro}
                    onChange={e => handleField('endereco.bairro', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Concelho *</label>
                  <input required value={formData.endereco.cidade}
                    onChange={e => handleField('endereco.cidade', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código postal *</label>
                  <input required value={formData.endereco.cep}
                    onChange={e => handleField('endereco.cep', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">País *</label>
                  <input required value={formData.endereco.pais}
                    onChange={e => handleField('endereco.pais', e.target.value)}
                    className="field" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Honorários e pagamento</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {formData.orcamentoId ? (
                  <div className="md:col-span-2 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800 space-y-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Definido pelo orçamento aceito pelo cliente</p>
                    <p>
                      <span className="font-semibold">{formatMoeda(Number(formData.valorHonorarios ?? 0), formData.moeda || 'EUR')}</span>
                      {' · '}
                      {formData.formaPagamento === 'parcelado' && (formData.numeroParcelas || 0) >= MIN_PARCELAS_CONTRATO
                        ? `${textoParcelasIguais(Number(formData.valorHonorarios ?? 0), formData.numeroParcelas || 1, formData.moeda || 'EUR')}, a 1ª na assinatura e as demais a cada ${formData.intervaloDias || 30} dias`
                        : formData.formaPagamento === 'personalizado'
                          ? (formData.parcelasPersonalizadas || []).map((p, i) => `${i + 1}.ª ${formatMoeda(Number(p.valor) || 0, formData.moeda || 'EUR')} em ${p.vencimento}`).join('; ')
                          : 'À vista, na assinatura'}
                    </p>
                    {textoCondicaoPagamento(formData) && <p className="text-gray-600">{textoCondicaoPagamento(formData)}</p>}
                    <p className="text-xs text-gray-500">Valor, moeda e parcelas não podem ser alterados aqui: o contrato segue o que o cliente escolheu ao aceitar o orçamento.</p>
                  </div>
                ) : (
                <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor dos honorários (€) *</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    required
                    value={formData.valorHonorarios ?? 600}
                    onChange={e => setFormData(prev => ({
                      ...prev,
                      valorHonorarios: parseFloat(e.target.value) || 0
                    }))}
                    className="field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forma de pagamento *</label>
                  <select
                    required
                    value={formData.formaPagamento || 'parcelado'}
                    onChange={e => changeFormaPagamento(e.target.value as FormaPagamentoContrato)}
                    className="field field--select"
                  >
                    {FORMAS_PAGAMENTO.map(op => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                </div>
                {formData.formaPagamento === 'parcelado' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Número de parcelas *</label>
                      <input
                        type="number"
                        required
                        min={MIN_PARCELAS_CONTRATO}
                        max={MAX_PARCELAS_CONTRATO}
                        value={formData.numeroParcelas ?? 3}
                        onChange={e => setFormData(prev => ({ ...prev, numeroParcelas: parseInt(e.target.value, 10) || 0 }))}
                        className="field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Intervalo entre parcelas (dias) *</label>
                      <input
                        type="number"
                        required
                        min={1}
                        max={365}
                        value={formData.intervaloDias ?? 30}
                        onChange={e => setFormData(prev => ({ ...prev, intervaloDias: parseInt(e.target.value, 10) || 0 }))}
                        className="field"
                      />
                    </div>
                    <p className="md:col-span-2 text-xs text-gray-500 -mt-2">
                      {(formData.numeroParcelas || 0) >= MIN_PARCELAS_CONTRATO
                        ? `${textoParcelasIguais(Number(formData.valorHonorarios ?? 0), formData.numeroParcelas || 1)}: a 1ª na assinatura e as demais a cada ${formData.intervaloDias || 30} dias.`
                        : `Informe de ${MIN_PARCELAS_CONTRATO} a ${MAX_PARCELAS_CONTRATO} parcelas.`}
                    </p>
                  </>
                )}
                {formData.formaPagamento === 'personalizado' && (
                  <div className="md:col-span-2 space-y-2">
                    {(formData.parcelasPersonalizadas || []).map((parcela, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-gray-600 w-8">{i + 1}.ª</span>
                        <input
                          type="number"
                          required
                          min={0.01}
                          step="0.01"
                          value={parcela.valor}
                          onChange={e => updateParcela(i, { valor: parseFloat(e.target.value) || 0 })}
                          className="field w-32"
                          aria-label={`Valor da parcela ${i + 1} (€)`}
                        />
                        <input
                          type="date"
                          required
                          value={parcela.vencimento}
                          onChange={e => updateParcela(i, { vencimento: e.target.value })}
                          className="field"
                          aria-label={`Vencimento da parcela ${i + 1}`}
                        />
                        <IconButton action="delete" label="Remover parcela" onClick={() => removeParcela(i)} disabled={(formData.parcelasPersonalizadas || []).length <= 1} className="inline-flex disabled:opacity-40" />
                      </div>
                    ))}
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={addParcela}
                        disabled={(formData.parcelasPersonalizadas || []).length >= MAX_PARCELAS_CONTRATO}
                        className="text-sm text-blue-600 hover:underline disabled:opacity-40"
                      >
                        + Adicionar parcela
                      </button>
                      <span className={`text-xs ${somaParcelasOk ? 'text-gray-500' : 'text-red-600 font-medium'}`}>
                        Soma: €{somaParcelas.toFixed(2)} de €{Number(formData.valorHonorarios ?? 0).toFixed(2)}
                        {somaParcelasOk ? '' : ' (precisa bater com o valor dos honorários)'}
                      </span>
                    </div>
                  </div>
                )}
                </>
                )}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo / detalhes de pagamento</label>
                  <input
                    type="text"
                    value={formData.tipoPagamento || ''}
                    onChange={e => handleField('tipoPagamento', e.target.value)}
                    placeholder="Ex.: Transferência bancária, MB Way..."
                    className="field"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Incluído na cláusula 3.1. O IBAN vem do cadastro do advogado ao gerar o PDF.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Assinatura do contrato</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Local *</label>
                  <input required value={formData.localAssinatura}
                    onChange={e => handleField('localAssinatura', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data do contrato *</label>
                  <input type="date" required value={formData.dataContrato}
                    onChange={e => handleField('dataContrato', e.target.value)}
                    className="field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modelo de contrato *</label>
                  <select
                    required
                    value={formData.modelo || 'judicial'}
                    onChange={e => {
                      const modelo = e.target.value as ContratoModelo;
                      // Idioma sem modelo ativo no novo tipo de contrato volta para pt.
                      const idiomaAtivo = formData.idioma === 'pt' || templates.some(
                        (tpl) => tpl.modelo === modelo && tpl.lang === formData.idioma && tpl.status === 'active'
                      );
                      setFormData(prev => ({ ...prev, modelo, idioma: idiomaAtivo ? prev.idioma : 'pt' }));
                    }}
                    className="field field--select"
                  >
                    {CONTRATO_MODELOS.map((modelo) => (
                      <option key={modelo} value={modelo}>{MODELO_LABEL[modelo]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Idioma do contrato *</label>
                  <select
                    required
                    value={formData.idioma || 'pt'}
                    onChange={e => handleField('idioma', e.target.value)}
                    className="field field--select"
                  >
                    <option value="pt">Português</option>
                    {(['en', 'es'] as TemplateLang[]).map((lang) => {
                      const isActive = templates.find(
                        (tpl) => tpl.modelo === (formData.modelo || 'judicial') && tpl.lang === lang
                      )?.status === 'active';
                      return (
                        <option key={lang} value={lang} disabled={!isActive}>
                          {TEMPLATE_LANG_LABEL[lang]}{!isActive ? ' (modelo não ativado)' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <Button variant="ghost" size="sm" type="button" onClick={() => setShowForm(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Salvar</span>
                  </>
                )}
              </Button>
              <Button size="sm" type="button" disabled={saving} onClick={saveAndGenerate}>
                <FileText className="w-4 h-4" />
                Salvar e gerar PDF
              </Button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <>
          <div className="mb-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome ou passaporte..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="field pl-10 pr-4"
              />
            </div>
            <select
              value={modeloFilter}
              onChange={e => setModeloFilter(e.target.value as ContratoModelo | '')}
              className="field field--select sm:w-56 sm:shrink-0"
              aria-label="Filtrar por modelo"
            >
              <option value="">Todos os modelos</option>
              {CONTRATO_MODELOS.map((modelo) => (
                <option key={modelo} value={modelo}>{MODELO_LABEL[modelo]}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
            </div>
          ) : contratos.length === 0 ? (
            <p className="text-gray-500 text-center py-12">Nenhum registro. Clique em &quot;Novo Cliente&quot;.</p>
          ) : (
            <div className="space-y-4">
              {contratos.map(c => (
                <div key={c._id} className="bg-white rounded-lg shadow p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900">{c.nome} {c.sobrenome}</p>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${MODELO_BADGE_CLASS[c.modelo || 'judicial']}`}>
                        {MODELO_LABEL[c.modelo || 'judicial']}
                      </span>
                      {c.status === 'signed' && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Assinado</span>
                      )}
                      {c.status === 'sent_for_signature' && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">Aguardando assinatura</span>
                      )}
                      {c.status === 'expired' && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Link expirado</span>
                      )}
                      {c.status === 'revoked' && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Link revogado</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {c.documentoTipo === 'cpf' ? 'CPF' : c.documentoTipo === 'nif' ? 'NIF' : 'Passaporte'}: {c.passaporteNumero}
                    </p>
                    <p className="text-xs text-gray-500">
                      Honorários: {formatMoeda(Number(c.valorHonorarios ?? 600), c.moeda || 'EUR')} · {labelFormaPagamento(c)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {c.endereco.cidade}, {c.endereco.pais}
                      {c.createdAt && ` · ${new Date(c.createdAt).toLocaleDateString('pt-PT')}`}
                    </p>
                  </div>
                  <div className="row-actions md:flex-wrap">
                    <ActionButton
                      action="edit"
                      onClick={() => openEdit(c)}
                    />
                    {c.status === 'signed' ? (
                      <ActionButton
                        action="download"
                        label="Baixar PDF"
                        onClick={() => c._id && downloadSignedPdf(c._id)}
                        disabled={generatingId === c._id}
                      />
                    ) : (
                      <ActionButton
                        icon={FileText}
                        label="Gerar PDF"
                        variant="primary"
                        onClick={() => c._id && openGeneratePdf(c._id)}
                        disabled={generatingId === c._id}
                      />
                    )}
                    {c.status === 'sent_for_signature' ? (
                      <ActionButton
                        icon={LinkIcon}
                        label="Link assinatura"
                        variant="warning"
                        onClick={() => c._id && openViewSignatureLink(c._id)}
                      />
                    ) : c.status !== 'signed' ? (
                      <ActionButton
                        action="send"
                        label="Assinatura"
                        onClick={() => c._id && openSendForSignature(c._id)}
                      />
                    ) : null}
                    <ActionButton
                      action="delete"
                      onClick={() => c._id && handleDelete(c._id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showAdvogadoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full overflow-y-auto p-6 transition-all duration-200 ${
            isAdvogadoModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-lg max-h-[90vh]'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Download className="w-5 h-5" /> Selecionar advogado
              </h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleAdvogadoModalExpanded}
                  className="icon-btn p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title={isAdvogadoModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isAdvogadoModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
                <IconButton action="close" onClick={() => { setShowAdvogadoModal(false); setIsAdvogadoModalExpanded(false); }} />
              </div>
            </div>
            <div className="space-y-3 mb-6">
              {advogadosList.map(adv => (
                <label
                  key={adv._id}
                  className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer ${
                    selectedAdvogadoId === adv._id ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <input
                    type="radio"
                    checked={selectedAdvogadoId === adv._id}
                    onChange={() => setSelectedAdvogadoId(adv._id)}
                  />
                  {adv.logo ? (
                    <img src={getAssetUrl(adv.logo)} alt="" className="w-12 h-12 object-contain border rounded" />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                      <User className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{adv.nome}</p>
                    <p className="text-sm text-gray-600">OAB {adv.oabNumero}/{adv.oabEstado}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="form-actions">
              <Button variant="ghost" size="sm"
                onClick={() => { setShowAdvogadoModal(false); setIsAdvogadoModalExpanded(false); }}>
                Cancelar
              </Button>
              <Button size="sm"
                onClick={generatePdf}
                disabled={!selectedAdvogadoId}>
                Gerar contrato PDF
              </Button>
            </div>
          </div>
        </div>
      )}

      {showSignatureModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full p-6 transition-all duration-200 ${
            isSignatureModalExpanded ? 'max-w-[95vw] max-h-[95vh] overflow-y-auto' : 'sm:max-w-lg max-h-[90vh] overflow-y-auto'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Send className="w-5 h-5" />
                {signatureModalMode === 'send' && !signUrlPreview ? 'Enviar para assinatura' : 'Link de assinatura'}
              </h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleSignatureModalExpanded}
                  className="icon-btn p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title={isSignatureModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isSignatureModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
                <IconButton action="close" onClick={() => { setShowSignatureModal(false); setIsSignatureModalExpanded(false); }} />
              </div>
            </div>

            {signatureModalMode === 'send' && !signUrlPreview ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  O cliente revisa e assina o contrato online, sem precisar de conta. Envie o link por e-mail ou
                  use <strong>Gerar link</strong> para copiar e mandar por WhatsApp ou outro canal.
                </p>
                {!signatureTemEmail && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">
                    Este contrato não tem e-mail do cliente cadastrado. Use Gerar link.
                  </p>
                )}
                <label className="block mb-4">
                  <span className="block text-sm font-medium text-gray-700 mb-1">Validade do link (dias)</span>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={expiresInDays}
                    onChange={e => setExpiresInDays(parseInt(e.target.value, 10) || 30)}
                    className="field"
                  />
                </label>
                <div className="form-actions">
                  <Button variant="ghost" size="sm"
                    onClick={() => { setShowSignatureModal(false); setIsSignatureModalExpanded(false); }}>
                    Cancelar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => confirmSendForSignature(false)}
                    disabled={sendingSignature}>
                    <LinkIcon className="w-4 h-4" /> Gerar link
                  </Button>
                  <Button
                    onClick={() => confirmSendForSignature(true)}
                    disabled={sendingSignature || !signatureTemEmail}
                    title={signatureTemEmail ? undefined : 'Sem e-mail cadastrado — use Gerar link'}>
                    {sendingSignature && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                    <Send className="w-4 h-4" /> Enviar por e-mail
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-2">Link de assinatura do cliente:</p>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    readOnly
                    value={signUrlPreview}
                    className="field flex-1 bg-gray-50"
                    onFocus={e => e.target.select()}
                  />
                  <IconButton action="copyLink" onClick={copySignUrl} />
                </div>
                {linkCopied && <p className="text-xs text-green-600 mb-4">Link copiado!</p>}
                {whatsappSignHref && (
                  <div className="mb-4">
                    <ActionButton
                      icon={MessageCircle}
                      label="Enviar pelo WhatsApp"
                      labelVisibility="always"
                      href={whatsappSignHref}
                    />
                  </div>
                )}
                <div className="form-actions">
                  <Button variant="ghost" size="sm" className="text-red-700 hover:bg-red-50 border-red-200"
                    onClick={revokeSignatureLink}>
                    <Ban className="w-4 h-4" /> Revogar link
                  </Button>
                  <Button
                    onClick={() => { setShowSignatureModal(false); setIsSignatureModalExpanded(false); }}>
                    Fechar
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {editingTemplateLang && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full overflow-y-auto p-6 transition-all duration-200 ${
            isTemplateModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-4xl max-h-[90vh]'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Languages className="w-5 h-5" /> Revisar modelo — {MODELO_LABEL[editingTemplateModelo]} · {TEMPLATE_LANG_LABEL[editingTemplateLang]}
              </h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleTemplateModalExpanded}
                  className="icon-btn p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  title={isTemplateModalExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isTemplateModalExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>
                <IconButton action="close" onClick={() => { setEditingTemplateLang(null); templateEditRef.current = null; setIsTemplateModalExpanded(false); }} />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              {editingTemplateLang === 'pt'
                ? 'Revise o texto do contrato. Esta é a versão atualmente usada nos contratos. Seções, cláusulas e itens podem ser incluídos ou removidos; a numeração é refeita sozinha.'
                : 'Revise o texto gerado pela IA. Seções, cláusulas e itens podem ser incluídos ou removidos; a numeração é refeita sozinha. Mudanças feitas no português só chegam aqui gerando de novo com IA.'}
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Os marcadores {'{{...}}'} são preenchidos automaticamente (nome, endereço, datas) e precisam ser mantidos. O trecho entre {'{{#if ...}}'} e {'{{else}}'} só aparece quando a condição vale, e o trecho entre {'{{else}}'} e {'{{/if}}'}, quando não vale. Use **texto** para negrito.
            </p>
            <div className="space-y-4">
              {templateSegments.map((seg, index) =>
                seg.kind === 'title' ? (
                  <div key={index}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{seg.label}</label>
                    <input
                      type="text"
                      value={seg.text}
                      onChange={(e) => updateTemplateSegment(index, e.target.value)}
                      className="field font-semibold"
                    />
                    {renderStructureActions(seg, index)}
                  </div>
                ) : (
                  <div key={index}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">{seg.label}</label>
                    <textarea
                      value={seg.text}
                      onChange={(e) => updateTemplateSegment(index, e.target.value)}
                      rows={seg.kind === 'item' ? 2 : seg.kind === 'rich' ? (seg.text.length > 160 ? 5 : 2) : 3}
                      className="field"
                    />
                    {renderStructureActions(seg, index)}
                  </div>
                )
              )}
            </div>
            <div className="form-actions mt-4">
              <Button variant="ghost" size="sm"
                type="button"
                onClick={() => { setEditingTemplateLang(null); templateEditRef.current = null; }}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleSaveTemplate}
                disabled={savingTemplate}>
                {savingTemplate ? 'Salvando...' : 'Salvar rascunho'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default AdminContrato;
